import { useState, useCallback, useRef, useEffect } from 'react';

export type MessageType = 'success' | 'error' | 'info' | 'warning';

export interface Message {
  type: MessageType;
  text: string;
}

export interface UseMessageOptions {
  /** Auto-dismiss timeout in milliseconds. Default: 4000. Set to 0 to disable. */
  timeout?: number;
}

export interface UseMessageReturn {
  /** Current message or null */
  message: Message | null;
  /** Show a success message */
  showSuccess: (text: string) => void;
  /** Show an error message */
  showError: (text: string) => void;
  /** Show an info message */
  showInfo: (text: string) => void;
  /** Show a warning message */
  showWarning: (text: string) => void;
  /** Show a message with custom type */
  showMessage: (type: MessageType, text: string) => void;
  /** Clear the current message */
  clearMessage: () => void;
}

/**
 * Custom hook for displaying temporary toast/notification messages.
 * Automatically dismisses messages after a timeout.
 *
 * @example
 * ```tsx
 * const { message, showSuccess, showError } = useMessage();
 *
 * const handleSave = async () => {
 *   try {
 *     await saveData();
 *     showSuccess('Saved successfully!');
 *   } catch (err) {
 *     showError(err.message || 'Failed to save');
 *   }
 * };
 *
 * return (
 *   <>
 *     {message && <Toast type={message.type}>{message.text}</Toast>}
 *     <button onClick={handleSave}>Save</button>
 *   </>
 * );
 * ```
 */
export function useMessage(options: UseMessageOptions = {}): UseMessageReturn {
  const { timeout = 4000 } = options;
  const [message, setMessage] = useState<Message | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear any pending timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const clearMessage = useCallback(() => {
    setMessage(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showMessage = useCallback((type: MessageType, text: string) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setMessage({ type, text });

    // Set auto-dismiss timeout if enabled
    if (timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        setMessage(null);
        timeoutRef.current = null;
      }, timeout);
    }
  }, [timeout]);

  const showSuccess = useCallback((text: string) => showMessage('success', text), [showMessage]);
  const showError = useCallback((text: string) => showMessage('error', text), [showMessage]);
  const showInfo = useCallback((text: string) => showMessage('info', text), [showMessage]);
  const showWarning = useCallback((text: string) => showMessage('warning', text), [showMessage]);

  return {
    message,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showMessage,
    clearMessage,
  };
}

export default useMessage;
